/* istanbul ignore file */
import { Listing } from 'graphql/types'
import logger from 'lib/logger'
import { TLocation } from 'lib/location'
import { getListingLoggingInfo } from 'lib/listing'
import {
  COLLECTION_NAME_DEFAULT,
  TQueryParams,
  toValidEventLogPrices,
} from 'lib/search'

export const openSearchEvent = ({ filtersCount }: { filtersCount?: number }) =>
  logger.action('listing-search-open', {
    filtersCount,
  })

export const paginateEvent = ({
  page,
  pagination,
  itemsPerPage,
  type,
  collection = COLLECTION_NAME_DEFAULT,
}: {
  page: number
  pagination: number
  itemsPerPage: number
  type: string
  collection: string
}) =>
  logger.action('listing-search-pagination', {
    page,
    pagination,
    itemsPerPage,
    type,
    collection,
  })

export const cardGalleryEvent = (listingId: string, button: 'prev' | 'next') =>
  logger.action('listing-card-photos', {
    listingId,
    button,
  })

export const cardClickEvent = (listingId: string) =>
  logger.action('listing-card-click', {
    listingId,
  })

export const searchInputFocusEvent = () =>
  logger.action('location-search-input-focus')

export const searchInputSelectEvent = (place: string) =>
  logger.action('listings-searchlocationinput-select', {
    place,
  })

export const searchInputSelectCityEvent = (city: string) =>
  logger.action('listings-searchlocationinput-city-select', {
    city,
  })

export const searchLocationOpenButtonEvent = () =>
  logger.action('listings-searchlocationinput-openbutton')

type TSearchLocationInputChangeEventProps = {
  city?: string
  citySlug?: string
  state?: string
  stateSlug?: string
  neighborhood?: string
  street?: string
  streetSlug?: string
  place?: string
  postalCode?: string
  lat?: number
  lng?: number
}

export const searchLocationInputChangeEvent = ({
  city,
  citySlug,
  state,
  stateSlug,
  neighborhood,
  street,
  streetSlug,
  place,
  postalCode,
  lat,
  lng,
}: TSearchLocationInputChangeEventProps) =>
  logger.action('listings-searchlocationinput-change', {
    city,
    citySlug,
    state,
    stateSlug,
    neighborhood,
    street,
    streetSlug,
    place,
    postalCode,
    lat,
    lng,
  })

export const searchInputSelectMyLocationEvent = () =>
  logger.action('listings-searchlocationinput-mylocation')

export const searchInputSelectMyLocationSelectEvent = (location: TLocation) =>
  logger.action('listings-searchlocationinput-mylocation-select', {
    city: location?.city,
    citySlug: location?.citySlug,
    coordinates: location?.coordinates,
    stateSlug: location?.stateSlug,
  })

export const searchInputClearEvent = () =>
  logger.action('listings-searchlocationinput-clear')

type TSearchResultEventProps = {
  city?: string
  region?: string
  filters?: TQueryParams
  filtersCount?: number
  listings?: Listing[] | null
  itemsCount?: number
  itemsDisplayed?: number
  pagetype?: string
  collection?: string
  isMap?: boolean
}

export const searchResultEvent = ({
  city,
  region,
  filters,
  filtersCount,
  itemsCount,
  itemsDisplayed,
  listings,
  collection = COLLECTION_NAME_DEFAULT,
  pagetype,
  isMap,
}: TSearchResultEventProps) => {
  const listingsIDs = listings?.map((listing) => listing.id)
  const listingsNeighborhoods = listings?.map(
    (listing) => listing.address?.neighborhood,
  )
  const listingsUniquesNeighborhoods = Array.from(
    new Set(listingsNeighborhoods),
  )

  logger.action('listing-search-results', {
    city,
    region: region?.toUpperCase(),
    listings,
    itemsCount,
    itemsDisplayed,
    filters: toValidEventLogPrices(filters),
    filtersCount,
    listing_pagetype: pagetype,
    content_ids: listingsIDs,
    neighborhood: listingsUniquesNeighborhoods.toString(),
    collection,
    isMap,
  })
}

type TSearchNotFoundEventProps = {
  filters?: TQueryParams
  filtersCount?: number
  collection?: string
}

export const searchNotFoundEvent = ({
  filters,
  filtersCount,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listing-search-not-found', {
    filters: toValidEventLogPrices(filters),
    filtersCount,
    collection,
  })

export const searchNotFoundCtaEvent = ({
  filters,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listing-search-not-found-cta', {
    filters: toValidEventLogPrices(filters),
    collection,
  })

export const searchNotFoundQEVCloseEvent = ({
  filters,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listing-search-not-found-qev-close', {
    filters: toValidEventLogPrices(filters),
    collection,
  })

export const searchNotFoundQEVCtaEvent = ({
  filters,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listing-search-not-found-qev-cta', {
    filters: toValidEventLogPrices(filters),
    collection,
  })

export const searchNotFoundSimilarsLoad = ({
  filters,
  filtersCount,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-load', {
    filters,
    filtersCount,
    collection,
  })

export const searchNotFoundSimilarsShow = ({
  filters,
  filtersCount,
  collection = COLLECTION_NAME_DEFAULT,
}: TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-show', {
    filters,
    filtersCount,
    collection,
  })

export const searchNotFoundSimilarsPhotos = ({
  photo,
  listing,
  filters,
  filtersCount,
  collection = COLLECTION_NAME_DEFAULT,
}: { photo: number; listing: Listing } & TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-photos', {
    ...listing,
    photo,
    filters,
    filtersCount,
    collection,
  })

export const searchNotFoundSimilarsFavorite = ({
  listing,
  filters,
  filtersCount,
  collection,
}: {
  listing: Listing
} & TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-favorite', {
    ...listing,
    filters,
    filtersCount,
    collection,
  })

export const searchNotFoundSimilarsClick = ({
  listing,
  filters,
  filtersCount,
  collection,
}: {
  listing: Listing
} & TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-click', {
    ...listing,
    filters,
    filtersCount,
    collection,
  })
export const searchNotFoundSimilarsButton = ({
  url,
  filters,
  filtersCount,
  collection,
}: {
  url?: string
} & TSearchNotFoundEventProps) =>
  logger.action('listings-search-not-found-similars-more-listings-button', {
    url,
    filters,
    filtersCount,
    collection,
  })

export const favoriteListingSearchEvent = (listing: Listing) =>
  logger.action('listing-search-favorite-listing', {
    ...getListingLoggingInfo(listing),
    favorited: true,
  })

export const searchFilterOpenEvent = () =>
  logger.action('listing-search-filter-open')

export const searchFilterCloseEvent = () =>
  logger.action('listing-search-filter-close')

export const searchFilterClearEvent = () =>
  logger.action('listing-search-filter-clear')

export const searchFilterApplyEvent = ({
  filters,
  filtersCount,
}: {
  filters: TQueryParams
  filtersCount?: number
}) =>
  logger.action('listing-search-filter-apply', {
    filters: toValidEventLogPrices(filters),
    filtersCount,
  })

export const searchFilterItemEvent = (name: string, value: string) =>
  logger.action('listing-search-filter-item', {
    name,
    value,
  })

export const searchOrdering = ({
  field,
  type,
}: {
  field: string | null
  type: string | null
}) =>
  logger.action('listing-search-ordering', {
    order: { field, type },
  })

export const searchCollectionClick = (name: string) =>
  logger.action('listing-search-collection-click', { name })

export const searchCollectionPaginate = (type: string) =>
  logger.action('listing-search-collection-paginate', { type })

export const searchCollectionIdealForYouQEVClose = () =>
  logger.action('collection-for-you-qev-close')

type TSearchCollectionUserOpenEventProps = {
  itemsCount?: number | undefined
  itemsDisplayed?: number | undefined
}

export const searchCollectionFavoritesOpen = ({
  itemsCount,
  itemsDisplayed,
}: TSearchCollectionUserOpenEventProps) =>
  logger.action('listing-search-collection-favorite-open', {
    itemsCount,
    itemsDisplayed,
  })

export const searchCollectionFavoritesLoginButton = () =>
  logger.action('collection-favorite-login-button')

export const searchCollectionIdealForYouOpen = ({
  itemsCount,
  itemsDisplayed,
}: TSearchCollectionUserOpenEventProps) =>
  logger.action('listing-search-collection-for-you-open', {
    itemsCount,
    itemsDisplayed,
  })

export const searchCollectionIdealForYouLoginButton = () =>
  logger.action('collection-for-you-login-button')

export const searchCollectionIdealForYouQEVButton = () =>
  logger.action('collection-for-you-qev-button')

export const openSearchMapEvent = (filtersCount?: number) =>
  logger.action('listing-search-map-open', { filtersCount })

export const searchMapZoomEvent = (zoom: number) =>
  logger.action('listing-search-map-zoom', {
    zoom,
  })

export const searchMapClusterClickEvent = ({
  lat,
  lng,
  count,
}: google.maps.LatLngLiteral & { count: number }) =>
  logger.action('listing-search-map-cluster', {
    lat,
    lng,
    count,
  })

export const searchMapMarkerClickEvent = (listingId: string) =>
  logger.action('listing-search-map-pin', {
    listingId,
  })

export const searchMapMultipleClickEvent = ({
  lat,
  lng,
  listingIds,
}: google.maps.LatLngLiteral & { listingIds: string[] }) =>
  logger.action('listing-search-map-multiple', {
    lat,
    lng,
    listingIds,
  })

export const searchMapNotFound = (filtersCount?: number) =>
  logger.action('listing-search-map-not-found', { filtersCount })

export const searchMapNotFoundNotification = (filtersCount?: number) =>
  logger.action('listing-search-map-not-found-notification', { filtersCount })

export const searchMapNotFoundNotificationClose = () =>
  logger.action('listing-search-map-not-found-notification-close')

export const searchMapNotFoundNotificationFilterButton = (
  filtersCount?: number,
) =>
  logger.action('listing-search-map-not-found-notification-filter-button', {
    filtersCount,
  })

export const openQEVSearchEvent = () => logger.action('listing-search-qev-open')

export const closeQEVSearchEvent = () =>
  logger.action('listing-search-qev-close')

export const searchToggleView = (currentView: 'map' | 'list') =>
  logger.action('listing-search-toggle-view', {
    currentView,
  })

export const searchCityInfoFAQExpand = ({
  city,
  question,
}: {
  city: string
  question: string
}) =>
  logger.action('listing-search-cityInfo-faq-expand', {
    city,
    question,
  })

export const searchCityInfoClick = ({
  href,
  view,
}: {
  href: string
  view: 'text' | 'faq'
}) =>
  logger.action('listing-search-cityInfo-click', {
    href,
    view,
  })
